import * as React from "react";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = BaseDialog.Root;

const DialogTrigger = BaseDialog.Trigger;

const DialogPortal = BaseDialog.Portal;

const DialogClose = BaseDialog.Close;

const DialogOverlay = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Backdrop>
>(({ className, ...props }, ref) => (
  <BaseDialog.Backdrop
    ref={ref}
    className={cn(
      "fixed inset-0 z-[var(--z-modal-backdrop)] bg-black/50 backdrop-blur-sm",
      "data-[open]:animate-[dialog-overlay-in_200ms_ease-out_both]",
      "data-[ending-style]:animate-[dialog-overlay-out_150ms_ease-in_both]",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof BaseDialog.Popup
> {
  showCloseButton?: boolean;
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  ({ className, children, showCloseButton = true, ...props }, ref) => (
    <BaseDialog.Portal>
      <DialogOverlay />
      <BaseDialog.Popup
        ref={ref}
        className={cn(
          "fixed inset-x-0 bottom-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]",
          "z-[var(--z-modal)] w-full sm:max-w-md",
          "max-h-[95vh] sm:max-h-[85vh]",
          "flex flex-col",
          "bg-global-primary border border-global-primary shadow-global-card",
          "rounded-t-2xl sm:rounded-2xl",
          "data-[open]:animate-[dialog-slide-in-bottom_250ms_ease-out_both] sm:data-[open]:animate-[dialog-content-in_200ms_ease-out_both]",
          "data-[ending-style]:animate-[dialog-slide-out-bottom_200ms_ease-in_both] sm:data-[ending-style]:animate-[dialog-content-out_150ms_ease-in_both]",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <BaseDialog.Close className="absolute right-4 top-3 sm:top-4 text-global-tertiary hover:text-global-primary transition-colors disabled:opacity-50 cursor-pointer">
            <X className="h-5 w-5" />
            <span className="sr-only">Cerrar</span>
          </BaseDialog.Close>
        )}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  ),
);
DialogContent.displayName = "DialogContent";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "px-4 sm:px-6 py-3 sm:py-4 border-b border-global-primary shrink-0",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex gap-3 px-4 sm:px-6 py-3 sm:py-4 shrink-0 border-t border-global-primary",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Title>
>(({ className, ...props }, ref) => (
  <BaseDialog.Title
    ref={ref}
    className={cn(
      "text-size-md sm:text-xl font-bold text-global-title",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Description>
>(({ className, ...props }, ref) => (
  <BaseDialog.Description
    ref={ref}
    className={cn("text-sm text-global-secondary mt-1", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
